import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import CupcakeOrderDetailsDisplay from '../../../components/CupcakeOrderDetailsDisplay';

const StepFourReview = ({ formData, prevStep }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const isCupcakes = formData.cakeShape === 'Cupcakes';

  const fullAddress = `${formData.addressLine1}, ${formData.addressLine2 ? formData.addressLine2 + ', ' : ''}${formData.city}, ${formData.state} ${formData.zipCode}`;

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    const payload = {
      ...formData,
      deliveryAddress: formData.isDelivery ? fullAddress : '',
    };
    delete payload.addressLine1;
    delete payload.addressLine2;
    delete payload.city;
    delete payload.state;
    delete payload.zipCode;
    delete payload.topperType;
    delete payload.baseCakeFlavor;
    delete payload.deliveryAddress;
    delete payload.deliveryCost;
    delete payload.baseCakePrice;
    delete payload.servings;

    try {
      const SERVER_URL =
        process.env.NODE_ENV === 'development'
          ? process.env.REACT_APP_SERVER_URL_LOCAL
          : process.env.REACT_APP_SERVER_URL;
      const response = await axios.post(`${SERVER_URL}/api/orders`, payload);
      navigate('/order-confirmation', { state: { orderId: response.data.orderId } });
    } catch (err) {
      console.error(err);
      setError('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 px-1 sm:px-0">
      <h2 className="text-xl sm:text-2xl font-bold text-center text-candy-primary">Review Your Order</h2>

      <section className="bg-white border rounded-lg p-3 sm:p-4 shadow-sm">
        <h3 className="text-base sm:text-lg font-semibold text-candy-primary mb-3">Customer Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-sm text-gray-800">
          <p><strong>Name:</strong> {formData.firstName} {formData.lastName}</p>
          <p><strong>Email:</strong> {formData.customerEmail}</p>
          <p><strong>Phone:</strong> {formData.customerPhone}</p>
          <p><strong>Pickup Date:</strong> {formData.eventDate}</p>
          <p><strong>Pickup Time:</strong> {formData.eventTime}</p>
        </div>
      </section>

      <section className="bg-white border rounded-lg p-3 sm:p-4 shadow-sm">
        <h3 className="text-base sm:text-lg font-semibold text-candy-primary mb-3">
          {isCupcakes ? 'Cupcake Details' : 'Cake Details'}
        </h3>

        {isCupcakes && (formData.cupcakeUnitConfigs?.length || 0) > 0 ? (
          <div className="mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-sm text-gray-800 mb-3">
              <p><strong>Product:</strong> Cupcakes</p>
              <p><strong>Quantity:</strong> {formData.cupcakeDozens || 0} dozen ({Math.round(Number(formData.cupcakeDozens || 0) * 12)} cupcakes)</p>
            </div>
            <CupcakeOrderDetailsDisplay
              cupcakeDozens={formData.cupcakeDozens}
              cupcakeUnitConfigs={formData.cupcakeUnitConfigs}
              cupcakeConfigMode={formData.cupcakeConfigMode}
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-sm text-gray-800">
            <p><strong>Shape:</strong> {formData.cakeShape}</p>
            <p><strong>Size:</strong> {formData.cakeSize}</p>
            <p><strong>Flavor:</strong> {formData.cakeFlavor}</p>
            <p><strong>Filling:</strong> {formData.fillingFlavor}</p>
            <p><strong>Frosting:</strong> {formData.frosting}</p>
            <p><strong>Decoration:</strong> {formData.decorationStyle}</p>
            <p><strong>Topper:</strong> {formData.cakeTopper}</p>
          </div>
        )}
      </section>

      {(formData.customerNotes || formData.customerInstructions) && (
        <section className="bg-white border rounded-lg p-3 sm:p-4 shadow-sm">
          <h3 className="text-base sm:text-lg font-semibold text-candy-primary mb-3">Special Instructions</h3>
          {formData.customerInstructions && (
            <p className="text-sm mb-2"><strong>Customer Instructions:</strong><br />{formData.customerInstructions}</p>
          )}
          {formData.customerNotes && (
            <p className="text-sm"><strong>Additional Notes:</strong><br />{formData.customerNotes}</p>
          )}
        </section>
      )}

      <section className="bg-white border rounded-lg p-3 sm:p-4 shadow-sm">
        <h3 className="text-base sm:text-lg font-semibold text-candy-primary mb-3">Pricing Summary</h3>
        <p className="text-sm text-gray-800">
          <strong>Total Price:</strong>{' '}
          <span className="text-[#a62c2b] font-bold">${formData.price}</span>
        </p>
      </section>

      {error && <p className="text-red-600 text-sm text-center">{error}</p>}

      <CardNav onBack={prevStep} onSubmit={handleSubmit} loading={loading} />
    </div>
  );
};

function CardNav({ onBack, onSubmit, loading }) {
  return (
    <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 mt-4 sm:mt-6">
      <button
        type="button"
        onClick={onBack}
        className="w-full sm:w-auto bg-gray-300 text-gray-800 font-semibold px-6 py-2.5 rounded-md hover:bg-gray-400"
      >
        ← Back
      </button>
      <button
        type="button"
        onClick={onSubmit}
        disabled={loading}
        className="w-full sm:w-auto bg-[#a62c2b] text-white font-semibold px-6 py-2.5 rounded-md hover:bg-[#8a1f1c] disabled:opacity-50"
      >
        {loading ? 'Placing Order...' : 'Place Order'}
      </button>
    </div>
  );
}

export default StepFourReview;
